import type * as HealthInsuranceConfig from './healthInsuranceConfig';

export interface IRhiV2ErrorCopy {
  title: string;
  body?: string;
}

export const RHI_V2_UNLISTED_ERROR_CODES = {
  PROPOSER_IDENTITY_MISMATCH: 'RHI_V2_PROPOSER_IDENTITY_MISMATCH',
} as const;

export const RHI_V2_NETWORK_ERROR = {
  title: "We couldn't load your details just now",
  body: 'Nothing you entered has been lost. Check your connection and try again.',
};

/** What the error screen needs: the copy, and the page "Try again" reopens. */
export interface IRhiV2ErrorScreenState {
  error?: IRhiV2ErrorCopy;
  retryTo?: string;
  /** A way forward other than retrying, e.g. Get fresh quotes. */
  action?: { label: string; to: string };
}

/** A quote whose HDFC add-on choices were erased (guide 2026-10-15 §8; the guide gives no copy). */
/**
 * When the proposal can be changed again, after a payment in progress (guide 2026-10-20, re-sending
 * the proposal on Proceed §3: `meta.retryAfterMs`; the guide gives no copy).
 */
export const rhiV2ProposalChangeableIn = (retryAfterMs: number) => {
  const minutes = Math.max(1, Math.ceil(retryAfterMs / 60_000));
  return `You can change your proposal again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
};

export const RHI_V2_QUOTE_UNPRICEABLE: IRhiV2ErrorCopy = {
  title: 'This quote can no longer be priced',
  body: 'Please get a fresh quote.',
};

/** The plan list after a refreshed quote; the guide gives no copy (guide 2026-10-14 §2). */
export const RHI_V2_PLAN_LIST_CHANGED_NOTICE: IRhiV2ErrorCopy = {
  title: 'The plan list has changed',
  body: 'Please choose your plan again.',
};

/** The same plan back under a new id; the guide gives no copy (guide 2026-10-14 §2). */
export const RHI_V2_PLAN_REFRESHED_NOTICE: IRhiV2ErrorCopy = {
  title: "This plan's price was refreshed",
  body: 'Please check the details and continue.',
};

/** A step of the flow opened with no quote in the session. */
export const RHI_V2_QUOTE_MISSING: IRhiV2ErrorCopy = {
  title: "We couldn't find your quote",
  body: 'Start again to pick your members and plan.',
};

/** Field messages. The proposal form and KYC word an unticked consent differently. */
export const RHI_V2_VALIDATION_COPY = {
  required: 'This is required',
  proposalConsent: 'Please tick to continue',
  kycConsent: 'Tick this to continue',
} as const;

/** Toast copy when the error code has none of its own. */
export const RHI_V2_TOAST_FALLBACK = {
  startPayment: 'Unable to start the payment. Please try again.',
  continue: 'Unable to continue. Please try again.',
} as const;

/** The error screen's way on from a quote that can't come back. */
export const RHI_V2_FRESH_QUOTE_LABEL = 'Get fresh quotes';

export const RHI_V2_GENERIC_ERROR: IRhiV2ErrorCopy = {
  title: 'Something went wrong',
  body: "A Benefits Consultant from Plum's Personal Insurance Team will reach out in the next 24 hours to help you with the steps.",
};

type RhiV2ErrorCodes = typeof HealthInsuranceConfig.RHI_V2_ERROR_CODES;
type RhiV2ErrorCodeValue = RhiV2ErrorCodes[keyof RhiV2ErrorCodes];

const KNOWN_ERROR_COPY: Record<RhiV2ErrorCodeValue, IRhiV2ErrorCopy> = {
  RHI_V2_PRICE_CHANGED: {
    title: 'The premium for this plan has changed',
    body: 'Review the new price before you continue.',
  },
  RHI_V2_PLAN_NOT_COMMITTED: {
    title: 'Pick your plan again to continue',
  },
  // Plan ids (guide 2026-10-14 §2): "not selectable" now only means Plum switched it off.
  RHI_V2_PLAN_NOT_SELECTABLE: {
    title: 'This plan is not available',
    body: 'Choose another plan to carry on.',
  },
  RHI_V2_PLAN_NO_LONGER_OFFERED: {
    title: 'This plan is no longer available',
  },
  RHI_V2_PLAN_ID_REPLACED: {
    title: 'This plan was re-quoted and has a new id',
  },
  RHI_V2_PLAN_LIST_UPDATING: {
    title: 'The plan list is being updated',
    body: 'Try again shortly.',
  },
  RHI_V2_PLAN_LIST_CHANGED: {
    title: 'The plan list has changed',
  },
  RHI_V2_PLAN_NOT_IN_QUOTE: {
    title: 'The plan list has changed',
  },
  RHI_V2_INSURER_PRICE_FAILED: {
    title: 'The insurer could not price this plan right now',
  },
  RHI_V2_ADDON_OPTION_REQUIRED: {
    title: 'Choose an option for this cover',
  },
  RHI_V2_ADDON_OPTION_INVALID: {
    title: 'That option is not offered for this cover',
  },
  RHI_V2_ADDON_COMBINATION_NOT_ALLOWED: {
    title: "This addon can't be added with your current selection",
  },
  RHI_V2_ADDON_NOT_APPLIED: {
    title: 'The insurer priced a different choice',
  },
  RHI_V2_KYC_REQUIRED: {
    title: 'Complete your KYC to continue',
    body: 'The insurer needs your identity verified before this proposal can go ahead.',
  },
  RHI_V2_PROPOSAL_COMPOSITION_CHANGED: {
    title: 'The insured members changed',
    body: 'This proposal cannot be continued. Start again to keep the same plan and price.',
  },
  RHI_V2_FORM_UNAVAILABLE: {
    title: 'The insurer is not responding',
    body: 'Nothing you entered has been lost. Try again in a moment.',
  },
  RHI_V2_PROPOSAL_NOT_FOUND: {
    title: "We couldn't find this application",
    body: 'Start a new quote to continue.',
  },
  RHI_V2_PROPOSAL_VERSION_CONFLICT: {
    title: 'This application was updated somewhere else',
    body: 'Check your answers and save again.',
  },
  RHI_V2_FORM_VERSION_CHANGED: {
    title: 'The insurer updated their questions',
    body: 'Review this page again before saving.',
  },
  RHI_V2_PROPOSAL_FROZEN: {
    title: 'Payment has already started',
    body: 'This proposal can no longer be edited.',
  },
  RHI_V2_ANSWER_INVALID: {
    title: 'Some answers need fixing',
    body: 'Nothing on this page was saved. Correct the marked answers and try again.',
  },
  RHI_V2_AFFIRMATION_NOT_PERMITTED: {
    title: 'This application cannot be confirmed yet',
    body: 'Finish the remaining questions first.',
  },
  RHI_V2_AFFIRMATION_STALE: {
    title: 'Your answers changed after you confirmed them',
    body: 'Review them once more and confirm again.',
  },
  RHI_V2_PROPOSAL_INCOMPLETE: {
    title: 'Some required answers are still missing',
  },
  RHI_V2_PROPOSAL_BLOCKED: {
    title: 'We cannot take this application online',
    body: 'A Benefits Consultant will call you to take it forward.',
  },
  RHI_V2_PROPOSAL_NOT_AFFIRMED: {
    title: 'Confirm your answers before submitting',
  },
  RHI_V2_PROPOSAL_PAYMENT_IN_PROGRESS: {
    title: 'A payment for this plan was started in the last 30 minutes',
    body: 'Complete it here.',
  },
  RHI_V2_PROPOSAL_SUBMIT_IN_PROGRESS: {
    title: 'This application is already being submitted',
    body: 'Give it a moment before trying again.',
  },
  RHI_V2_PROPOSAL_REJECTED: {
    title: 'The insurer could not accept this application',
    body: 'A Benefits Consultant will call you to talk through the options.',
  },
  RHI_V2_PROPOSAL_UNCONFIRMED: {
    title: 'The insurer has not confirmed this application yet',
    body: "We'll let you know as soon as they do.",
  },
  RHI_V2_D2C_PROFILE_REQUIRED: {
    title: 'We need a few details about you first',
    body: 'Complete your profile to carry on with this quote.',
  },
  RHI_V2_QUOTE_SESSION_FORBIDDEN: {
    title: 'This quote belongs to someone else',
    body: 'Start a new quote to continue.',
  },
  RHI_V2_INVALID_QUOTE_REQUEST: {
    title: "We couldn't price a plan with these details",
    body: 'Check the members and dates of birth you entered and try again.',
  },
  RHI_V2_QUOTE_SESSION_NOT_FOUND: {
    title: 'This quote has expired',
    body: 'Start again to see current prices.',
  },
  RHI_V2_QUOTE_NOT_RECALCULABLE: {
    title: "This quote can't be repriced",
    body: 'Start a new quote to see current prices.',
  },
  RHI_V2_PAYMENT_ATTEMPT_EXISTS: {
    title: 'A payment is already in progress',
    body: 'Wait for it to finish before starting another.',
  },
  RHI_V2_PAYMENT_LINK_UNAVAILABLE: {
    title: 'We could not open the payment page',
    body: 'Please try again in a few minutes.',
  },
  RHI_V2_RISKCOVRY_REQUEST_FAILED: {
    title: 'The insurer did not respond',
    body: 'Please try again in a few minutes.',
  },
  RHI_V2_POLICY_DOCUMENT_NOT_READY: {
    title: 'Your policy document is still being prepared',
    body: "We'll email it to you as soon as it is ready.",
  },
  RHI_V2_PROPOSAL_NOT_SUBMITTED: {
    title: 'This application has not been submitted yet',
    body: 'Finish and submit it to continue.',
  },
  RHI_V2_PROPOSAL_AMENDED: {
    title: 'This application was changed after you confirmed it',
    body: 'Review the changes and confirm again.',
  },
  RHI_V2_PLAN_SUPERSEDED: {
    title: 'This plan has been replaced by a newer one',
    body: 'Pick a plan again to see current cover and pricing.',
  },
  RHI_V2_PRICE_UNAVAILABLE: {
    title: 'We could not get a price for this plan',
    body: 'Try another plan, or come back in a few minutes.',
  },
  RHI_V2_KYC_PLAN_NOT_COMMITTED: {
    title: 'Pick your plan again to continue',
  },
  RHI_V2_KYC_NOT_AVAILABLE: {
    title: 'This insurer does not need an identity check',
  },
  RHI_V2_KYC_MISCONFIGURED: {
    title: 'Something went wrong on our side',
    body: "This isn't about your details. Please try again in a little while.",
  },
  RHI_V2_KYC_UPSTREAM_REJECTED: {
    title: "The insurer couldn't match these details",
    body: 'Check them and try again.',
  },
  RHI_V2_KYC_CONSENT_REQUIRED: {
    title: 'Your consent is needed to continue',
  },
  RHI_V2_KYC_UPSTREAM_UNAVAILABLE: {
    title: "The insurer isn't responding",
    body: 'Nothing you entered has been lost. Try again in a moment.',
  },
  RHI_V2_KYC_ALREADY_VERIFIED: {
    title: 'Your identity is already verified',
  },
  RHI_V2_KYC_OTP_INVALID: {
    title: "That code didn't work",
    body: 'Check the SMS and enter it again.',
  },
  RHI_V2_KYC_OTP_REQUIRED: {
    title: 'This code has expired',
    body: 'Verify your details again to get a new code.',
  },
  RHI_V2_KYC_NOT_VERIFIED: {
    title: 'Verify your identity to continue',
  },
  RHI_V2_DOCUMENT_INVALID: {
    title: "This file can't be uploaded",
    body: 'Check its type and size, and choose another file.',
  },
  RHI_V2_DOCUMENT_REJECTED: {
    title: "The insurer didn't accept this file",
    body: 'Choose another file and upload it again.',
  },
  RHI_V2_DOCUMENT_QUESTION_INVALID: {
    title: "This file can't be uploaded here",
    body: 'Save this page first, then upload the file.',
  },
  RHI_V2_DOCUMENT_STALE: {
    title: 'Please upload your documents again',
    body: 'The insurer needs them again before you confirm.',
  },
  RHI_V2_PROPOSAL_REVALIDATION_FAILED: {
    title: "We couldn't recheck your proposal with the insurer",
    body: 'Please try again in a few minutes.',
  },
  RHI_V2_KYC_IDENTITY_MISMATCH: {
    title: "Some details don't match your KYC",
  },
  RHI_V2_KYC_CARRY_FORWARD_UNCONFIRMED: {
    title: "We couldn't confirm your verification",
    body: 'Please try again.',
  },
  RHI_V2_KYC_PLAN_NO_LONGER_AVAILABLE: {
    title: "This plan isn't available for you",
    body: "Based on your verified date of birth, the insurer can't offer this plan. Other plans are ready for you.",
  },
  RHI_V2_QUOTE_LOCKED: {
    title: 'This plan has already been paid for',
    body: 'Your payment has gone through, so these details can no longer be changed.',
  },
  RHI_V2_PURCHASE_IN_PROGRESS: {
    title: 'You may have already paid',
    body: 'Another purchase for you may already be paid or in progress.',
  },
  RHI_V2_PAYMENT_PRECHECK_UNAVAILABLE: {
    title: "Couldn't start the payment",
    body: 'Please try again.',
  },
  RHI_V2_QUOTE_EXPIRED: {
    title: 'This quote has expired',
    body: 'Please get a fresh quote.',
  },
  RHI_V2_FANOUT_BUDGET_EXCEEDED: {
    title: "You've checked a lot of quotes",
    body: 'Try again in a little while.',
  },
  RBAC_ACCESS_DENIED: {
    title: "You don't have access to this",
    body: 'Sign in with the account that started this application.',
  },
};

const UNLISTED_ERROR_COPY: Record<string, IRhiV2ErrorCopy> = {
  [RHI_V2_UNLISTED_ERROR_CODES.PROPOSER_IDENTITY_MISMATCH]: {
    title: "This doesn't match the date of birth on your Plum profile",
    body: 'Use the same date of birth your employer has on record for you.',
  },
};

export const RHI_V2_ERROR_COPY: Record<string, IRhiV2ErrorCopy> = {
  ...KNOWN_ERROR_COPY,
  ...UNLISTED_ERROR_COPY,
};
